import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loanAccountsApi } from '../api/loanAccounts';
import { otherTransactionsApi } from '../api/otherTransactions';
import type { LoanAccount, LoanAccountLoanType, LoanAccountSecurity, OtherFinancialTransaction, OtherFinancialTransactionType, PaymentMode } from '@coreledger/shared-types';
import Pagination from '../components/Pagination';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';
import { FeatureGate } from '../components/FeatureGate';

// Real, dated records for the 7 Bank Reconciliation "Suggested Ledger"
// labels that used to be purely cosmetic (Loan Received/Repayment, Capital
// Infusion/Withdrawal, Interest Income, Asset Purchase/Sale — see
// CLAUDE.md's own note) — reachable both from a confirmed bank-statement
// label and, here, entered directly for a transaction that never came
// through a bank statement at all.
//
// The Loans tab lists LoanAccount rows (one per lender/loan relationship),
// not flat transactions — clicking one drills into LoanAccountDetailPage.tsx
// for that account's own transaction history, the same list→detail
// drill-down shape BankingPage.tsx→BankTransactionsPage.tsx already uses
// for statement→entries. This replaced an earlier flat-transaction design
// once a real CA review found it couldn't support per-loan classification
// (secured/unsecured, loan type) or a running per-loan balance without
// re-entering that classification on every single transaction row — see
// CLAUDE.md's Loans & Capital note. Capital & Other stays flat/unchanged —
// none of those gaps apply to it.

const fmt = (n: number) => '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
const today = () => new Date().toISOString().slice(0, 10);

const PAYMENT_MODES: PaymentMode[] = ['cash', 'upi', 'bank_transfer', 'cheque', 'other'];
const MODE_LABELS: Record<string, string> = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };

export const LOAN_TYPE_LABELS: Record<LoanAccountLoanType, string> = {
  term_loan: 'Term Loan',
  working_capital: 'Working Capital / CC / OD',
  vehicle_loan: 'Vehicle Loan',
  loan_against_property: 'Loan Against Property',
  personal_loan: 'Personal Loan',
  other: 'Other',
};

const OTHER_TYPE_LABELS: Record<OtherFinancialTransactionType, string> = {
  capital_infusion: 'Capital Infusion',
  capital_withdrawal: 'Capital Withdrawal / Drawings',
  interest_income: 'Interest Income',
  asset_purchase: 'Asset Purchase',
  asset_sale: 'Sale of Asset',
};
// Same debit/credit side convention as ledger-suggestion.ts's OTHER_LABELS
// on the backend — kept here only for the amount-sign hint in the form, the
// server is the single source of truth for what actually gets booked.
const OTHER_TYPE_SIDE: Record<OtherFinancialTransactionType, 'debit' | 'credit'> = {
  capital_infusion: 'credit', capital_withdrawal: 'debit', interest_income: 'credit',
  asset_purchase: 'debit', asset_sale: 'credit',
};

function emptyAccountForm(): Partial<LoanAccount> {
  return { lenderName: '', loanType: 'other', security: 'unsecured', isRelatedParty: false };
}
function emptyOtherForm(): Partial<OtherFinancialTransaction> {
  return { type: 'capital_infusion', date: today(), amount: 0, mode: 'bank_transfer' };
}

export default function LoansCapitalPage() {
  const navigate = useNavigate();
  const { canDelete } = useRole();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'loans' | 'other'>('loans');
  const [page, setPage] = useState(1);
  const [accountModal, setAccountModal] = useState<null | 'create' | LoanAccount>(null);
  const [otherModal, setOtherModal] = useState<null | 'create' | OtherFinancialTransaction>(null);
  const [accountForm, setAccountForm] = useState<Partial<LoanAccount>>(emptyAccountForm());
  const [otherForm, setOtherForm] = useState<Partial<OtherFinancialTransaction>>(emptyOtherForm());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: accountData, isLoading: accountLoading } = useQuery({
    queryKey: ['loan-accounts', page],
    queryFn: () => loanAccountsApi.list(page, 50),
    staleTime: 30 * 1000,
    enabled: tab === 'loans',
  });
  const { data: otherData, isLoading: otherLoading } = useQuery({
    queryKey: ['other-transactions', page],
    queryFn: () => otherTransactionsApi.list(page, 50),
    staleTime: 30 * 1000,
    enabled: tab === 'other',
  });

  const accounts = accountData?.data ?? [];
  const others = otherData?.data ?? [];

  function switchTab(next: 'loans' | 'other') {
    setTab(next);
    setPage(1);
  }

  async function saveAccount() {
    if (!accountForm.lenderName?.trim()) { setError('Lender name is required'); return; }
    setError(null); setSaving(true);
    try {
      if (accountModal && accountModal !== 'create') await loanAccountsApi.update(accountModal.id, accountForm);
      else await loanAccountsApi.create(accountForm);
      qc.invalidateQueries({ queryKey: ['loan-accounts'] });
      qc.refetchQueries({ type: 'all' });
      setAccountModal(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function saveOther() {
    if (!otherForm.amount || otherForm.amount <= 0) { setError('Enter a valid amount'); return; }
    if (!otherForm.date) { setError('Date is required'); return; }
    setError(null); setSaving(true);
    try {
      if (otherModal && otherModal !== 'create') await otherTransactionsApi.update(otherModal.id, otherForm);
      else await otherTransactionsApi.create(otherForm);
      qc.invalidateQueries({ queryKey: ['other-transactions'] });
      qc.refetchQueries({ type: 'all' });
      setOtherModal(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const deleteAccount = useMutation({
    mutationFn: loanAccountsApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['loan-accounts'] }); qc.refetchQueries({ type: 'all' }); },
  });
  const deleteOther = useMutation({
    mutationFn: otherTransactionsApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['other-transactions'] }); qc.refetchQueries({ type: 'all' }); },
  });

  return (
    <FeatureGate feature="balanceSheet">
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Loans &amp; Capital</h1>
        {tab === 'loans'
          ? <button className="btn btn-primary" onClick={() => { setAccountForm(emptyAccountForm()); setAccountModal('create'); }}>+ New Loan Account</button>
          : <button className="btn btn-primary" onClick={() => { setOtherForm(emptyOtherForm()); setOtherModal('create'); }}>+ Record Transaction</button>}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
        <button
          onClick={() => switchTab('loans')}
          style={{ padding: '0.6rem 1rem', border: 'none', background: 'none', cursor: 'pointer', fontWeight: tab === 'loans' ? 700 : 500, color: tab === 'loans' ? 'var(--primary)' : 'var(--text-2)', borderBottom: tab === 'loans' ? '2px solid var(--primary)' : '2px solid transparent' }}
        >Loans</button>
        <button
          onClick={() => switchTab('other')}
          style={{ padding: '0.6rem 1rem', border: 'none', background: 'none', cursor: 'pointer', fontWeight: tab === 'other' ? 700 : 500, color: tab === 'other' ? 'var(--primary)' : 'var(--text-2)', borderBottom: tab === 'other' ? '2px solid var(--primary)' : '2px solid transparent' }}
        >Capital &amp; Other</button>
      </div>

      {tab === 'loans' && (
        <div className="card">
          <table>
            <thead>
              <tr><th>Lender</th><th>Type</th><th>Security</th><th style={{ textAlign: 'right' }}>Outstanding Principal</th><th></th></tr>
            </thead>
            <tbody>
              {accountLoading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : accounts.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No loan accounts yet</td></tr>
              ) : accounts.map(a => (
                <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/loans-capital/loans/${a.id}`)}>
                  <td>
                    {a.lenderName}
                    {a.isRelatedParty && <span className="badge badge-orange" style={{ marginLeft: '0.4rem' }}>Related Party</span>}
                  </td>
                  <td>{LOAN_TYPE_LABELS[a.loanType]}</td>
                  <td><span className={`badge ${a.security === 'secured' ? 'badge-blue' : 'badge-gray'}`}>{a.security === 'secured' ? 'Secured' : 'Unsecured'}</span></td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(a.outstandingPrincipal)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/loans-capital/loans/${a.id}`)}>View</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setAccountForm(a); setAccountModal(a); }}>Edit</button>
                      {canDelete && <button className="btn btn-danger btn-sm" disabled={deleteAccount.isPending} onClick={async () => { if (await confirm({ message: `Delete "${a.lenderName}"? This is only allowed once it has no transactions.`, danger: true })) deleteAccount.mutate(a.id); }}>Del</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {accountData && <Pagination page={page} totalPages={accountData.meta.totalPages} onChange={setPage} />}
        </div>
      )}

      {tab === 'other' && (
        <div className="card">
          <table>
            <thead>
              <tr><th>Date</th><th>Type</th><th>Counterparty</th><th style={{ textAlign: 'right' }}>Amount</th><th>Mode</th><th>Reference</th><th></th></tr>
            </thead>
            <tbody>
              {otherLoading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : others.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No capital/other transactions yet</td></tr>
              ) : others.map(ot => (
                <tr key={ot.id}>
                  <td>{ot.date}</td>
                  <td><span className={`badge ${OTHER_TYPE_SIDE[ot.type] === 'credit' ? 'badge-green' : 'badge-orange'}`}>{OTHER_TYPE_LABELS[ot.type]}</span></td>
                  <td>{ot.counterparty ?? '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(ot.amount)}</td>
                  <td>{MODE_LABELS[ot.mode]}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{ot.reference ?? '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setOtherForm(ot); setOtherModal(ot); }}>Edit</button>
                      {canDelete && <button className="btn btn-danger btn-sm" disabled={deleteOther.isPending} onClick={async () => { if (await confirm({ message: `Delete this transaction?`, danger: true })) deleteOther.mutate(ot.id); }}>Del</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {otherData && <Pagination page={page} totalPages={otherData.meta.totalPages} onChange={setPage} />}
        </div>
      )}

      {/* Loan Account Modal */}
      {accountModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setAccountModal(null); }}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">{accountModal === 'create' ? 'New Loan Account' : 'Edit Loan Account'}</span>
              <button className="modal-close" onClick={() => setAccountModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box" style={{ marginBottom: '0.75rem' }}>{error}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  Lender Name *
                  <input value={accountForm.lenderName ?? ''} onChange={e => setAccountForm(f => ({ ...f, lenderName: e.target.value }))} placeholder="e.g. HDFC Bank, Director loan" style={{ marginTop: '0.3rem' }} />
                </label>
                <div className="form-row form-row-2">
                  <label>Loan Type
                    <select value={accountForm.loanType} onChange={e => setAccountForm(f => ({ ...f, loanType: e.target.value as LoanAccountLoanType }))}>
                      {(Object.keys(LOAN_TYPE_LABELS) as LoanAccountLoanType[]).map(t => <option key={t} value={t}>{LOAN_TYPE_LABELS[t]}</option>)}
                    </select>
                  </label>
                  <label>Security
                    <select value={accountForm.security} onChange={e => setAccountForm(f => ({ ...f, security: e.target.value as LoanAccountSecurity }))}>
                      <option value="unsecured">Unsecured</option>
                      <option value="secured">Secured</option>
                    </select>
                  </label>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                  <input type="checkbox" checked={accountForm.isRelatedParty ?? false} onChange={e => setAccountForm(f => ({ ...f, isRelatedParty: e.target.checked }))} />
                  Related-party loan (Director / shareholder)
                </label>
                <div className="form-row form-row-2">
                  <label>Sanctioned Amount (₹, optional)
                    <input type="number" min={0} step={0.01} value={accountForm.sanctionedAmount ?? ''} onChange={e => setAccountForm(f => ({ ...f, sanctionedAmount: e.target.value ? parseFloat(e.target.value) : undefined }))} placeholder="0.00" />
                  </label>
                  <label>Interest Rate (% p.a., optional)
                    <input type="number" min={0} step={0.01} value={accountForm.interestRate ?? ''} onChange={e => setAccountForm(f => ({ ...f, interestRate: e.target.value ? parseFloat(e.target.value) : undefined }))} placeholder="0.00" />
                  </label>
                </div>
                <div className="form-row form-row-2">
                  <label>Loan Account Number (optional)
                    <input value={accountForm.accountNumber ?? ''} onChange={e => setAccountForm(f => ({ ...f, accountNumber: e.target.value }))} />
                  </label>
                  <label>Start Date (optional)
                    <input type="date" value={accountForm.startDate ?? ''} onChange={e => setAccountForm(f => ({ ...f, startDate: e.target.value }))} />
                  </label>
                </div>
                <label>Notes (optional)
                  <input value={accountForm.notes ?? ''} onChange={e => setAccountForm(f => ({ ...f, notes: e.target.value }))} />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAccountModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveAccount} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capital/Other Transaction Modal */}
      {otherModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setOtherModal(null); }}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <span className="modal-title">{otherModal === 'create' ? 'Record Transaction' : 'Edit Transaction'}</span>
              <button className="modal-close" onClick={() => setOtherModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box" style={{ marginBottom: '0.75rem' }}>{error}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  Type
                  <select value={otherForm.type} onChange={e => setOtherForm(f => ({ ...f, type: e.target.value as OtherFinancialTransactionType }))} style={{ marginTop: '0.3rem', width: '100%' }}>
                    {(Object.keys(OTHER_TYPE_LABELS) as OtherFinancialTransactionType[]).map(t => <option key={t} value={t}>{OTHER_TYPE_LABELS[t]}</option>)}
                  </select>
                </label>
                <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  Counterparty (optional)
                  <input value={otherForm.counterparty ?? ''} onChange={e => setOtherForm(f => ({ ...f, counterparty: e.target.value }))} placeholder="Partner name, asset description, etc." style={{ marginTop: '0.3rem' }} />
                </label>
                <div className="form-row form-row-2">
                  <label>Amount (₹) *
                    <input type="number" min={0.01} step={0.01} value={otherForm.amount || ''} onChange={e => setOtherForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
                  </label>
                  <label>Date *
                    <input type="date" value={otherForm.date} onChange={e => setOtherForm(f => ({ ...f, date: e.target.value }))} />
                  </label>
                </div>
                <div className="form-row form-row-2">
                  <label>Mode
                    <select value={otherForm.mode} onChange={e => setOtherForm(f => ({ ...f, mode: e.target.value as PaymentMode }))}>
                      {PAYMENT_MODES.map(m => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                    </select>
                  </label>
                  <label>Reference (optional)
                    <input value={otherForm.reference ?? ''} onChange={e => setOtherForm(f => ({ ...f, reference: e.target.value }))} placeholder="Cheque / UTR / TXN #" />
                  </label>
                </div>
                <label>Notes (optional)
                  <input value={otherForm.notes ?? ''} onChange={e => setOtherForm(f => ({ ...f, notes: e.target.value }))} />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setOtherModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveOther} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </FeatureGate>
  );
}
